import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Pipe({
  name: 'highlight',
})
export class HighlightPipe implements PipeTransform {
  // note: This creates a new DOMParser instance and uses it to parse the value string into an HTML document (doc). This allows for DOM manipulation.
  private _domParser = new DOMParser();

  constructor(private _sanitizer: DomSanitizer) {}

  public transform(valueToTransform: string, highlightTerm: string): SafeHtml {
    if (!highlightTerm) {
      return valueToTransform;
    }

    // note: Uses a regular expression to escape special characters in the searchTerm to ensure it doesn't interfere with the regex used for highlighting.
    const escapedSearchTerm = highlightTerm.replace(
      /[.*+?^${}()|[\]\\]/g,
      '\\$&',
    );
    // note: Creates a new case-insensitive regular expression to match the searchTerm in the HTML content.
    const regex = new RegExp(`(${escapedSearchTerm})`, 'gi');
    const doc = this._domParser.parseFromString(valueToTransform, 'text/html');

    this._highlightTextNodes(doc.body, regex);

    return this._sanitizer.bypassSecurityTrustHtml(doc.body.innerHTML);
  }

  private _highlightTextNodes(node: Node, regex: RegExp): void {
    // note: If the current node is a text node (Node.TEXT_NODE), get its value. If the value matches the regex, proceed to highlight it.
    if (node.nodeType === Node.TEXT_NODE) {
      // note: Replace occurrences of the search term in the text node's value with a span element wrapping the matched term.
      const nodeValue = node.nodeValue || '';
      const newNodeValue = nodeValue.replace(
        regex,
        (match) => `<span class="highlight">${match}</span>`,
      );

      // note: Only proceed if the new node value is different from the original node value (i.e., if there was a match).
      if (newNodeValue !== nodeValue) {
        // note: Create a temporary div container and set its inner HTML to the new node value (which contains the highlighted spans).
        const tempContainer = document.createElement('div');
        tempContainer.innerHTML = newNodeValue;

        if (node.parentNode) {
          // note: Insert the children of the temporary container (which are now the highlighted spans) before the original text node and then remove the original text node. This ensures the text node is replaced with the correct highlighted content without nesting spans.
          while (tempContainer.firstChild) {
            node.parentNode.insertBefore(tempContainer.firstChild, node);
          }
          node.parentNode.removeChild(node);
        }
      }
    } else if (
      node.nodeType === Node.ELEMENT_NODE &&
      node.nodeName !== 'SCRIPT' &&
      node.nodeName !== 'STYLE'
    ) {
      // note: If the node is an element node (not a text node) and is not a SCRIPT or STYLE tag, recursively call highlightTextNodes on each of its child nodes using Array.from to safely iterate over the child nodes.
      Array.from(node.childNodes).forEach((child) =>
        this._highlightTextNodes(child, regex),
      );
    }
  }
}
